import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Eye, ChevronLeft, FileDown, Mail, 
  Calendar, CreditCard, Car, PawPrint, Phone, MapPin,
  Clock, Filter, Download, Loader2, ChevronRight,
  FileText, Shield, User, Building2, Plus, X, Check,
  Stethoscope, Hospital, Home, Navigation
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kundentyp-Badge Komponente (außerhalb definiert)
const CustomerTypeBadge = ({ type, isAnonymized }) => {
  const styles = {
    member: 'bg-blue-100 text-blue-700',
    website: 'bg-green-100 text-green-700',
    manual: 'bg-purple-100 text-purple-700'
  };
  const labels = {
    member: 'Mitglied',
    website: 'Website',
    manual: 'Manuell'
  };
  return (
    <div className="flex items-center gap-1">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-100 text-gray-700'}`}>
        {labels[type] || type}
      </span>
      {isAnonymized && (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          Anonym
        </span>
      )}
    </div>
  );
};

// Modal für manuellen Kunden (außerhalb definiert)
const AddManualCustomerModal = ({ password, onClose, onSuccess }) => {
  const [anonymizedName, setAnonymizedName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const validateFormat = (value) => {
    const pattern = /^[A-Za-zÄÖÜäöü]{3}[A-Za-zÄÖÜäöü]-\d{1,4}$/;
    if (!pattern.test(value)) {
      return 'Format: 3 Buchstaben Vorname + 1 Buchstabe Nachname + Zahl (z.B. MaxM-127)';
    }
    const number = parseInt(value.split('-')[1]);
    if (number < 1 || number > 1000) {
      return 'Zahl muss zwischen 1 und 1000 liegen';
    }
    return '';
  };

  const handleNameChange = (e) => {
    let value = e.target.value.toUpperCase();
    value = value.replace(/[^A-ZÄÖÜa-zäöü0-9-]/g, '');
    
    if (value.length === 4 && !value.includes('-')) {
      value = value + '-';
    }
    
    const parts = value.split('-');
    if (parts[0] && parts[0].length > 4) {
      parts[0] = parts[0].substring(0, 4);
      value = parts.join('-');
    }
    
    if (parts[1] && parts[1].length > 4) {
      parts[1] = parts[1].substring(0, 4);
      value = parts.join('-');
    }
    
    setAnonymizedName(value);
    setValidationError(value.length > 0 ? validateFormat(value) : '');
  };

  const handleSubmit = async () => {
    const error = validateFormat(anonymizedName);
    if (error) {
      setValidationError(error);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/customer-registry/manual?password=${password}`, {
        anonymizedName,
        notes
      });
      toast.success('Kunde erfolgreich angelegt');
      onClose();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anlegen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <User className="h-6 w-6" />
              <div>
                <h3 className="font-bold">Manuellen Kunden anlegen</h3>
                <p className="text-sm text-purple-200">Anonymisiert (DSGVO-konform)</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Shield className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">DSGVO-Hinweis</p>
                <p>Da keine Einwilligung vorliegt, werden nur anonymisierte Daten gespeichert.</p>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Anonymisierte Kennung *</Label>
            <Input
              value={anonymizedName}
              onChange={handleNameChange}
              placeholder="z.B. MAXM-127"
              className={`mt-1 font-mono text-lg tracking-wider ${validationError ? 'border-red-500' : ''}`}
              maxLength={9}
            />
            {validationError && (
              <p className="text-red-500 text-xs mt-1">{validationError}</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              <strong>Format:</strong> 3 Buchstaben Vorname + 1 Buchstabe Nachname + Zahl<br />
              <strong>Beispiele:</strong> MAXM-127, ANNS-42, TOMB-999
            </p>
          </div>

          <div>
            <Label className="text-sm font-medium">Interne Notizen (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="z.B. Regelmäßiger Kunde, Hund Bello"
              className="mt-1"
            />
          </div>
        </div>

        <div className="border-t p-4 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !anonymizedName || validationError}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
          >
            {isSubmitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird angelegt...</>
            ) : (
              <><Plus className="h-4 w-4 mr-2" /> Kunde anlegen</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Fahrt erfassen Modal Komponente
const RecordTripModal = ({ customer, onClose, onSuccess, password }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tripData, setTripData] = useState({
    petId: customer.pets?.[0]?.id || '',
    tripType: 'tierarzt', // 'tierarzt' oder 'tierklinik'
    zone: 'innerorts', // 'innerorts' oder 'ausserorts'
    destination: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Kosten berechnen
  const calculateCosts = () => {
    const anfahrt = tripData.zone === 'innerorts' ? 1.50 : 3.50;
    const klinikZuschlag = tripData.tripType === 'tierklinik' ? 5.00 : 0;
    return {
      anfahrt,
      klinikZuschlag,
      total: anfahrt + klinikZuschlag
    };
  };

  const costs = calculateCosts();

  const handleSubmit = async () => {
    if (!tripData.petId) {
      toast.error('Bitte wählen Sie ein Tier aus');
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedPet = customer.pets?.find(p => p.id === tripData.petId);
      
      await axios.post(`${API}/admin/customer-registry/${customer.id}/record-trip?password=${password}`, {
        ...tripData,
        petName: selectedPet?.petName || 'Unbekannt',
        memberNumber: customer.memberNumber,
        customerName: customer.name,
        customerEmail: customer.email,
        costs: costs
      });
      
      toast.success(`Fahrt erfasst: ${costs.total.toFixed(2)} € Zusatzkosten`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erfassen der Fahrt');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Car className="h-6 w-6" />
              <div>
                <h3 className="font-bold text-lg">Flatrate-Fahrt erfassen</h3>
                <p className="text-blue-100 text-sm">{customer.name} - #{customer.memberNumber}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-5">
          {/* Tier auswählen */}
          <div>
            <Label className="text-sm font-semibold text-gray-700">Tier auswählen *</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {customer.pets?.map(pet => (
                <div
                  key={pet.id}
                  onClick={() => setTripData(prev => ({ ...prev, petId: pet.id }))}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-3 ${
                    tripData.petId === pet.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <PawPrint className={`h-5 w-5 ${tripData.petId === pet.id ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">{pet.petName}</p>
                    <p className="text-xs text-gray-500">{pet.petType}</p>
                  </div>
                  {tripData.petId === pet.id && <Check className="h-5 w-5 text-blue-600 ml-auto" />}
                </div>
              ))}
            </div>
          </div>

          {/* Fahrttyp */}
          <div>
            <Label className="text-sm font-semibold text-gray-700">Fahrttyp *</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div
                onClick={() => setTripData(prev => ({ ...prev, tripType: 'tierarzt' }))}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                  tripData.tripType === 'tierarzt'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <Stethoscope className={`h-8 w-8 mx-auto mb-2 ${tripData.tripType === 'tierarzt' ? 'text-green-600' : 'text-gray-400'}`} />
                <p className="font-medium">Tierarzt</p>
                <p className="text-xs text-gray-500">Keine Pauschale</p>
              </div>
              <div
                onClick={() => setTripData(prev => ({ ...prev, tripType: 'tierklinik' }))}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                  tripData.tripType === 'tierklinik'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
              >
                <Hospital className={`h-8 w-8 mx-auto mb-2 ${tripData.tripType === 'tierklinik' ? 'text-orange-600' : 'text-gray-400'}`} />
                <p className="font-medium">Tierklinik</p>
                <p className="text-xs text-orange-600 font-semibold">+5,00 € Pauschale</p>
              </div>
            </div>
          </div>

          {/* Anfahrtszone */}
          <div>
            <Label className="text-sm font-semibold text-gray-700">Anfahrtszone *</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div
                onClick={() => setTripData(prev => ({ ...prev, zone: 'innerorts' }))}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                  tripData.zone === 'innerorts'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <Home className={`h-8 w-8 mx-auto mb-2 ${tripData.zone === 'innerorts' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className="font-medium">Innerorts</p>
                <p className="text-xs text-blue-600 font-semibold">1,50 € Anfahrt</p>
              </div>
              <div
                onClick={() => setTripData(prev => ({ ...prev, zone: 'ausserorts' }))}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                  tripData.zone === 'ausserorts'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <Navigation className={`h-8 w-8 mx-auto mb-2 ${tripData.zone === 'ausserorts' ? 'text-purple-600' : 'text-gray-400'}`} />
                <p className="font-medium">Außerorts</p>
                <p className="text-xs text-purple-600 font-semibold">3,50 € Anfahrt</p>
              </div>
            </div>
          </div>

          {/* Datum & Ziel */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-gray-700">Datum</Label>
              <Input
                type="date"
                value={tripData.date}
                onChange={(e) => setTripData(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700">Ziel (optional)</Label>
              <Input
                placeholder="z.B. Dr. Müller"
                value={tripData.destination}
                onChange={(e) => setTripData(prev => ({ ...prev, destination: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          {/* Kostenübersicht */}
          <div className="bg-gray-50 rounded-lg p-4 border">
            <h4 className="font-semibold text-gray-700 mb-3">Kostenübersicht</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Anfahrtskosten ({tripData.zone === 'innerorts' ? 'Innerorts' : 'Außerorts'}):</span>
                <span className="font-medium">{costs.anfahrt.toFixed(2)} €</span>
              </div>
              {costs.klinikZuschlag > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tierklinik-Pauschale:</span>
                  <span className="font-medium">{costs.klinikZuschlag.toFixed(2)} €</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between text-base">
                <span className="font-semibold">Gesamt:</span>
                <span className="font-bold text-green-600">{costs.total.toFixed(2)} €</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Abbrechen
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !tripData.petId}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird gespeichert...</>
            ) : (
              <><Check className="h-4 w-4 mr-2" /> Fahrt erfassen ({costs.total.toFixed(2)} €)</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CustomerRegistry = ({ password, onBack }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingPdf, setIsSendingPdf] = useState(false);
  const [view, setView] = useState('list'); // 'list', 'detail'
  const [filterType, setFilterType] = useState('all'); // 'all', 'members', 'website', 'manual'
  const [showTripModal, setShowTripModal] = useState(false);
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [stats, setStats] = useState(null);

  // Kunden laden
  useEffect(() => {
    loadCustomers();
    loadStats();
  }, []);

  const loadCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customer-registry?password=${password}`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/customer-registry/stats?password=${password}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Kundendaten laden
  const loadCustomerData = async (customerId, customerType) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/customer-registry/${customerId}/data?password=${password}&type=${customerType}`
      );
      setCustomerData(response.data);
      setView('detail');
    } catch (error) {
      toast.error('Fehler beim Laden der Kundendaten');
    } finally {
      setIsLoading(false);
    }
  };

  // DSGVO-Datenexport als PDF senden
  const handleSendDataExport = async () => {
    if (!customerData) return;
    
    setIsSendingPdf(true);
    try {
      const response = await axios.post(
        `${API}/admin/customer-registry/${customerData.id}/export-pdf?password=${password}&type=${customerData.customerType}`
      );
      toast.success('DSGVO-Datenexport wurde per E-Mail versendet');
    } catch (error) {
      toast.error('Fehler beim Senden des Datenexports');
    } finally {
      setIsSendingPdf(false);
    }
  };

  // Manuellen Kunden löschen
  const handleDeleteManualCustomer = async (customerId) => {
    if (!window.confirm('Diesen manuellen Kunden wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/admin/customer-registry/manual/${customerId}?password=${password}`);
      toast.success('Kunde gelöscht');
      loadCustomers();
      loadStats();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Filtern
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm) ||
      customer.memberNumber?.toString().includes(searchTerm) ||
      customer.customerNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    if (filterType === 'members') return matchesSearch && customer.customerType === 'member';
    if (filterType === 'website') return matchesSearch && customer.customerType === 'website';
    if (filterType === 'manual') return matchesSearch && customer.customerType === 'manual';
    return matchesSearch;
  });

  // Datum formatieren
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Währung formatieren
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Liste der Kunden
  if (view === 'list') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack}>
              <ChevronLeft className="h-4 w-4 mr-2" /> Zurück
            </Button>
            <div>
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <Users className="h-6 w-6 mr-2 text-blue-600" />
                Kundenregister
              </h2>
              <p className="text-sm text-gray-500">DSGVO-konforme Kundenübersicht und Datenexport</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => setShowAddManualModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" /> Manuell hinzufügen
            </Button>
            <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
              <Shield className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-700 font-medium">DSGVO-konform</span>
            </div>
          </div>
        </div>

        {/* Statistiken */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Gesamt</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Mitglieder</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.members}</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Website</p>
                    <p className="text-2xl font-bold text-green-600">{stats.websiteCustomers}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Manuell</p>
                    <p className="text-2xl font-bold text-purple-600">{stats.manualCustomers}</p>
                  </div>
                  <User className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter & Suche */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Kundennummer, Name, E-Mail oder Telefon..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="customer-search"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'all', label: 'Alle', count: customers.length, color: '' },
                  { id: 'members', label: 'Mitglieder', count: customers.filter(c => c.customerType === 'member').length, color: 'blue' },
                  { id: 'website', label: 'Website', count: customers.filter(c => c.customerType === 'website').length, color: 'green' },
                  { id: 'manual', label: 'Manuell', count: customers.filter(c => c.customerType === 'manual').length, color: 'purple' }
                ].map(filter => (
                  <Button
                    key={filter.id}
                    variant={filterType === filter.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterType(filter.id)}
                    className={filterType === filter.id && filter.color ? `bg-${filter.color}-600 hover:bg-${filter.color}-700` : ''}
                  >
                    {filter.label} ({filter.count})
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kundenliste */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {filteredCustomers.length} Kunde{filteredCustomers.length !== 1 ? 'n' : ''} gefunden
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Keine Kunden gefunden</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCustomers.map(customer => (
                  <div
                    key={`${customer.customerType}-${customer.id}`}
                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                      customer.isAnonymized 
                        ? 'bg-purple-50 hover:bg-purple-100 border border-purple-200' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      if (!customer.isAnonymized) {
                        setSelectedCustomer(customer);
                        loadCustomerData(customer.id, customer.customerType);
                      }
                    }}
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                        customer.customerType === 'member' 
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                          : customer.customerType === 'website'
                          ? 'bg-gradient-to-br from-green-500 to-green-600'
                          : 'bg-gradient-to-br from-purple-500 to-purple-600'
                      }`}>
                        {customer.isAnonymized ? '?' : customer.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {customer.customerNumber && (
                            <span className="text-xs bg-gray-700 text-white px-2 py-0.5 rounded font-mono">
                              {customer.customerNumber}
                            </span>
                          )}
                          <p className={`font-semibold ${customer.isAnonymized ? 'font-mono text-purple-700' : 'text-gray-800'}`}>
                            {customer.name}
                          </p>
                          <CustomerTypeBadge type={customer.customerType} isAnonymized={customer.isAnonymized} />
                          {customer.memberNumber && (
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                              M-{customer.memberNumber}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          {customer.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {customer.email}
                            </span>
                          )}
                          {customer.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {customer.phone}
                            </span>
                          )}
                          {customer.notes && (
                            <span className="flex items-center gap-1 text-purple-600">
                              <FileText className="h-3 w-3" /> {customer.notes}
                            </span>
                          )}
                          {customer.isAnonymized && (
                            <span className="text-xs text-purple-500 italic">Keine persönlichen Daten gespeichert</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm">
                        <p className="text-gray-500">Kunde seit</p>
                        <p className="font-medium">{formatDate(customer.createdAt)}</p>
                      </div>
                      {customer.isAnonymized ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteManualCustomer(customer.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal für manuellen Kunden */}
        {showAddManualModal && (
          <AddManualCustomerModal 
            password={password}
            onClose={() => setShowAddManualModal(false)}
            onSuccess={() => {
              loadCustomers();
              loadStats();
            }}
          />
        )}
      </div>
    );
  }

  // Kundendetail-Ansicht
  if (view === 'detail' && customerData) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setView('list'); setCustomerData(null); }}>
              <ChevronLeft className="h-4 w-4 mr-2" /> Zurück zur Liste
            </Button>
          </div>
          <div className="flex gap-2">
            {customerData.customerType === 'member' && customerData.pets?.length > 0 && (
              <Button 
                onClick={() => setShowTripModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="record-trip-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Fahrt erfassen
              </Button>
            )}
            <Button 
              onClick={handleSendDataExport}
              disabled={isSendingPdf}
              className="bg-green-600 hover:bg-green-700"
              data-testid="export-pdf-btn"
            >
              {isSendingPdf ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird gesendet...</>
              ) : (
                <><FileDown className="h-4 w-4 mr-2" /> DSGVO-Datenexport senden</>
              )}
            </Button>
          </div>
        </div>

        {/* Trip Modal */}
        {showTripModal && customerData && (
          <RecordTripModal
            customer={customerData}
            onClose={() => setShowTripModal(false)}
            onSuccess={() => loadCustomerData(customerData.id, customerData.customerType)}
            password={password}
          />
        )}

        {/* Kundenprofil */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                  {customerData.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    {customerData.name}
                    <CustomerTypeBadge type={customerData.customerType} />
                  </CardTitle>
                  <CardDescription className="flex items-center gap-4 mt-1">
                    {customerData.memberNumber && (
                      <span className="font-medium text-blue-600">Mitglied #{customerData.memberNumber}</span>
                    )}
                    <span>Kunde seit: {formatDate(customerData.createdAt)}</span>
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Kontaktdaten */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <User className="h-4 w-4" /> Kontaktdaten
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  {customerData.email && (
                    <p className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span>{customerData.email}</span>
                    </p>
                  )}
                  {customerData.phone && (
                    <p className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span>{customerData.phone}</span>
                    </p>
                  )}
                  {customerData.address && (
                    <p className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{customerData.address}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Statistik */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Übersicht
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{customerData.statistics?.totalBookings || 0}</p>
                    <p className="text-xs text-blue-700">Buchungen</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(customerData.statistics?.totalPayments || 0)}</p>
                    <p className="text-xs text-green-700">Zahlungen</p>
                  </div>
                  {customerData.customerType === 'member' && (
                    <>
                      <div className="bg-orange-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-orange-600">{customerData.statistics?.totalTripsUsed || 0}</p>
                        <p className="text-xs text-orange-700">Fahrten genutzt</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{customerData.pets?.length || 0}</p>
                        <p className="text-xs text-purple-700">Registrierte Tiere</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tiere (für Mitglieder) */}
        {customerData.pets && customerData.pets.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PawPrint className="h-5 w-5 text-orange-500" />
                Registrierte Tiere ({customerData.pets.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customerData.pets.map((pet, idx) => (
                  <div key={idx} className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-3">
                      <PawPrint className="h-8 w-8 text-orange-500" />
                      <div>
                        <p className="font-semibold text-gray-800">{pet.petName || 'Unbenannt'}</p>
                        <p className="text-sm text-gray-500">{pet.petType || 'Unbekannt'}</p>
                      </div>
                    </div>
                    {pet.notes && (
                      <p className="mt-2 text-xs text-gray-600 bg-white p-2 rounded">{pet.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Buchungen/Fahrten */}
        {customerData.bookings && customerData.bookings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Car className="h-5 w-5 text-blue-500" />
                Buchungen & Fahrten ({customerData.bookings.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left">Datum</th>
                      <th className="p-3 text-left">Tier</th>
                      <th className="p-3 text-left">Ziel</th>
                      <th className="p-3 text-left">Typ</th>
                      <th className="p-3 text-right">Betrag</th>
                      <th className="p-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerData.bookings.map((booking, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3">{formatDate(booking.date)}</td>
                        <td className="p-3">{booking.petName || '-'}</td>
                        <td className="p-3">{booking.destination || '-'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            booking.tripType === 'emergency' ? 'bg-red-100 text-red-700' :
                            booking.tripType === 'long' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {booking.tripType === 'emergency' ? 'Notfall' :
                             booking.tripType === 'long' ? 'Langstrecke' : 'Kurzstrecke'}
                          </span>
                        </td>
                        <td className="p-3 text-right font-medium">{formatCurrency(booking.amount)}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {booking.status === 'completed' ? 'Abgeschlossen' :
                             booking.status === 'confirmed' ? 'Bestätigt' :
                             booking.status === 'cancelled' ? 'Storniert' : 'Ausstehend'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Zahlungen */}
        {customerData.payments && customerData.payments.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-500" />
                Zahlungen ({customerData.payments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left">Datum</th>
                      <th className="p-3 text-left">Beschreibung</th>
                      <th className="p-3 text-left">Kategorie</th>
                      <th className="p-3 text-right">Betrag</th>
                      <th className="p-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerData.payments.map((payment, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3">{formatDate(payment.date)}</td>
                        <td className="p-3">{payment.description}</td>
                        <td className="p-3">
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                            {payment.type === 'flatrate' ? 'Flatrate' :
                             payment.type === 'booking' ? 'Buchung' : payment.type}
                          </span>
                        </td>
                        <td className="p-3 text-right font-medium text-green-600">
                          +{formatCurrency(payment.amount)}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            payment.confirmed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {payment.confirmed ? 'Bestätigt' : 'Ausstehend'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* DSGVO Info */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-800">DSGVO-Datenauskunft</h4>
                <p className="text-sm text-green-700 mt-1">
                  Gemäß Art. 15 DSGVO hat jeder Kunde das Recht auf Auskunft über seine gespeicherten personenbezogenen Daten.
                  Klicken Sie auf &ldquo;DSGVO-Datenexport senden&rdquo;, um alle Daten als PDF an Ihre E-Mail zu senden und diese
                  an den Kunden weiterzuleiten.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default CustomerRegistry;
